'use client';

/**
 * @packageDocumentation
 * @category Hook
 */

import { createReorderEngine, type ReorderEngineOptions } from 'BomdisoftComponents/reorder/engine';
import { useStore } from '@bomdisoft/store/react';
import { type DragEvent, type KeyboardEvent, useState } from 'react';

export type { ReorderAxis, ReorderEngineOptions, ReorderTreatment } from 'BomdisoftComponents/reorder/engine';

/**
 * The reorder gesture, without any opinion about markup.
 *
 * Exists as a hook because the two surfaces cannot share a wrapper: a board owns
 * its own tiles, while a DataGrid header cell is a sticky grid child that must
 * stay exactly where it is. The hook hands each surface props to spread onto the
 * elements it already renders.
 *
 * Built on the platform's drag and drop. `dragover` fires on the element under
 * the cursor, so the destination needs no geometry, which is what keeps the
 * indicator on the cursor: the obvious hand-rolled rule, the target's midpoint,
 * measured 117px behind on a 242px tile.
 */
export const useReorder = (options: ReorderEngineOptions) => {
  const [engine] = useState(() => createReorderEngine(options));
  engine.setOptions(options);
  const { dragging, held, mark, slotIndex } = useStore(engine.store);

  return {
    dragging,
    /** Spread onto the handle. It carries `draggable`, so it is the one thing
     *  that starts a drag, and it is the focus target for the keyboard path. */
    getGripProps: (id: string) => ({
      'data-held': held?.id === id ? '' : undefined,
      draggable: true,
      onClick: () => engine.togglePick(id),
      onDragEnd: engine.endDrag,
      onDragStart: (event: DragEvent<HTMLElement>) => engine.dragStart(id, event),
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => engine.keyDown(id, event),
    }),
    /** Spread onto the element the surface already renders for this item. */
    getItemProps: (id: string) => ({
      'data-dragging': dragging === id ? '' : undefined,
      'data-drop': mark?.id === id ? mark.side : undefined,
      'data-held': held?.id === id ? '' : undefined,
      onClick: () => engine.place(id, held),
      // BOTH, not just dragover. The spec marks an element a valid drop target
      // when either handler calls preventDefault, and the two do not fire
      // equally reliably: a real drag driven through automation produced one
      // dragover and several dragenters, and released over an element that had
      // only ever seen dragenter, so the drop was refused and the column
      // snapped back. Handling both is what makes the drop land.
      onDragEnter: (event: DragEvent<HTMLElement>) => engine.dragOver(id, event),
      onDragOver: (event: DragEvent<HTMLElement>) => engine.dragOver(id, event),
      onDrop: (event: DragEvent<HTMLElement>) => engine.drop(id, event),
      ref: (element: HTMLElement | null) => engine.register(id, element),
    }),
    /** Spread onto the reserved slot AND onto the container, so the destination
     *  the reader is aiming at accepts the drop and the board's empty space does
     *  too. Neither is an item, so both land on the indicated destination. */
    getSurfaceProps: () => ({
      onDragEnter: engine.surfaceDragOver,
      onDragOver: engine.surfaceDragOver,
      onDrop: engine.surfaceDrop,
    }),
    held: held?.id ?? null,
    slotIndex: (options.treatment ?? 'slot') === 'slot' ? slotIndex : null,
  };
};
